"use client";

import "streamdown/styles.css";
import "katex/dist/katex.css";
import "./streaming-markdown.css";

import { memo, useMemo } from "react";

import {
  defaultRehypePlugins,
  type Components,
  type StreamdownProps,
} from "streamdown";

import { InlineCitation } from "@/components/ai-elements/inline-citation";
import { MessageResponse } from "@/components/ai-elements/message";
import { cn } from "@/lib/utils";
import {
  CHAT_PLUGINS,
  CHAT_REMARK_PLUGINS,
  useChatTranslations,
} from "@/lib/ai/markdown-config";

export type CitedSource = { url: string; title?: string };

type HastNode = {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

function rehypeCitations(options: { urls: Set<string> }) {
  const walk = (node: HastNode) => {
    for (const child of node.children ?? []) {
      const href = child.properties?.href;

      if (
        child.type === "element" &&
        child.tagName === "a" &&
        typeof href === "string" &&
        options.urls.has(href)
      ) {
        child.tagName = "cite-source";
        child.properties = { url: href };
        child.children = [];
      } else {
        walk(child);
      }
    }
  };

  return (tree: HastNode) => walk(tree);
}

function hostnameOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function isSameSite(url: string) {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

const LINK_SAFETY = { enabled: true, onLinkCheck: isSameSite };

const GARBAGE_CHARS = /[\p{Co}\p{Cn}]/gu;

function CitationChip({
  url,
  onCite,
}: {
  url: string;
  onCite?: (url: string) => void;
}) {
  const label = hostnameOf(url);
  const className =
    "mx-0.5 inline-flex items-center rounded-full bg-muted px-2 py-px align-baseline text-xs text-muted-foreground no-underline transition-colors hover:bg-accent hover:text-foreground";

  return (
    <InlineCitation>
      {onCite ? (
        <button type="button" className={className} onClick={() => onCite(url)}>
          {label}
        </button>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className={className}
        >
          {label}
        </a>
      )}
    </InlineCitation>
  );
}

function StreamingMarkdownImpl({
  text,
  className,
  streaming,
  sources,
  onCite,
}: {
  text: string;
  className?: string;

  streaming?: boolean;

  sources?: CitedSource[];

  onCite?: (url: string) => void;
}) {
  const translations = useChatTranslations();
  const hasSources = Boolean(sources?.length);
  const rehypePlugins = useMemo<StreamdownProps["rehypePlugins"]>(
    () =>
      hasSources
        ? [
            ...Object.values(defaultRehypePlugins),
            [rehypeCitations, { urls: new Set(sources!.map((item) => item.url)) }],
          ]
        : undefined,
    [hasSources, sources],
  );
  const components = useMemo(
    () =>
      hasSources
        ?
          ({
            "cite-source": ({ url }: { url?: string }) =>
              url ? <CitationChip url={url} onCite={onCite} /> : null,
          } as Components)
        : undefined,
    [hasSources, onCite],
  );

  return (
    <MessageResponse

      key={sources?.length ?? 0}
      className={cn("chat-markdown", className)}
      rehypePlugins={rehypePlugins}
      components={components}
      linkSafety={LINK_SAFETY}
      plugins={CHAT_PLUGINS}
      remarkPlugins={CHAT_REMARK_PLUGINS}
      translations={translations}
      animated
      isAnimating={streaming}
    >
      {text.replace(GARBAGE_CHARS, "")}
    </MessageResponse>
  );
}

export const StreamingMarkdown = memo(StreamingMarkdownImpl);
